import {getTestProps} from '@benchling/js-test-utils';
import {sleep} from '@benchling/js-utils';
import {ActionIcon, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useLayoutEffect, useEffect, useRef, useState} from 'react';
import {useInView} from 'react-intersection-observer';

import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {LatestUserMessageInfo} from '../ChatScrollTypes';

// When auto-scrolling to the latest user message, we leave this many pixels of room above that
// user message.
const TOP_MARGIN_PX = 16;
const SCROLLBAR_WIDTH = 8; // keep in sync with variables.less:@scrollbar-width
const SCROLL_BOTTOM_THRESHOLD_PX = 16;

interface UseChatScrollDownButtonInput {
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
}

interface UseChatScrollDownButtonResult {
  bottomSentinelRef: (node: Element | null) => void;
  scrollContainerRef: (node: HTMLDivElement | null) => void;
  scrollToBottom: () => void;
  showScrollDownButton: boolean;
}

function useChatScrollDownButton({
  scrollContainerRef,
}: UseChatScrollDownButtonInput): UseChatScrollDownButtonResult {
  // Only enable if the browser has support for the IntersectionObserver
  const intersectionObserverSupported = typeof IntersectionObserver !== 'undefined';

  // useInView needs an element for the root node, so we can't directly pass scrollContainerRef, since it
  // can be null initially. Instead, setScrollContainerRef stores the node in state, and useInView skips
  // until that state value is non-null.
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const {ref: bottomSentinelRef, inView: isBottomSentinelInView} = useInView({
    root: scrollContainer,
    rootMargin: `0px 0px ${SCROLL_BOTTOM_THRESHOLD_PX}px 0px`,
    initialInView: true,
    threshold: 0,
    skip: scrollContainer == null || !intersectionObserverSupported,
  });
  const setScrollContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      setScrollContainer(node);
    },
    [scrollContainerRef]
  );

  const [suppressScrollDownButton, setSuppressScrollDownButton] = useState(false);

  const scrollToBottom = useCallback(() => {
    const scrollElement = scrollContainerRef.current;
    if (scrollElement) {
      setSuppressScrollDownButton(true);
      scrollElement.addEventListener(
        'scrollend',
        () => {
          setSuppressScrollDownButton(false);
        },
        {once: true}
      );
      scrollElement.scrollTo({
        top: Math.max(scrollElement.scrollHeight - scrollElement.clientHeight, 0),
        behavior: 'smooth',
      });
    }
  }, [scrollContainerRef]);

  const showScrollDownButton =
    intersectionObserverSupported && !isBottomSentinelInView && !suppressScrollDownButton;

  return {
    bottomSentinelRef,
    scrollContainerRef: setScrollContainerRef,
    scrollToBottom,
    showScrollDownButton,
  };
}

interface ChatScrollContainerProps {
  autoScroll: boolean;
  chatMessagesElement: React.ReactNode;
  chatInputElement: React.ReactNode;
  latestUserMessageInfo: LatestUserMessageInfo | null;
  /**
   * An optional ref used to persist scroll position across remounts. When the component
   * unmounts (e.g. due to sidepanel expand/collapse), the current scrollTop is saved here.
   * On the next mount, the scroll position is restored and the initial auto-scroll-to-bottom
   * is skipped.
   */
  savedScrollPositionRef?: React.MutableRefObject<number>;
}

/**
 * Layout component for chat interfaces. The input area is pinned to the bottom of the container
 * and the chat messages are scrollable.
 *
 * This component also implements auto-scrolling as the user sends new messages. Each time a
 * message is sent, we scroll to keep that message that top of the visible space (with some margin)
 * and add whitespace below via min-height.
 *
 * This component currently enforces that all chats have a max width of 800px, though this could
 * be decoupled in the future if necessary. Importantly, the scroll bar should be on the right
 * side of the outer container (not the right side of the messages) when the max-width applies.
 */
export default function ChatScrollContainer({
  autoScroll,
  chatMessagesElement,
  chatInputElement,
  latestUserMessageInfo,
  savedScrollPositionRef,
}: ChatScrollContainerProps): JSX.Element {
  const chatThreadScrollContainerElement = useRef<HTMLDivElement | null>(null);
  const chatThreadContentsElement = useRef<HTMLDivElement | null>(null);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  const {width: scrollContainerWidth, height: scrollContainerHeight} = useResizeObserver<HTMLDivElement>({
    ref: chatThreadScrollContainerElement,
  });
  const {
    bottomSentinelRef,
    scrollContainerRef: setScrollContainerRef,
    scrollToBottom,
    showScrollDownButton,
  } = useChatScrollDownButton({
    scrollContainerRef: chatThreadScrollContainerElement,
  });
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);

  const lastScrolledIndex = useRef<number>(-1);
  const pendingScrollRestoreRef = useRef(false);

  // Save scroll position on unmount via layout effect so the value is available to the
  // new component's useLayoutEffect (layout cleanups run before layout effects).
  useLayoutEffect(
    () => () => {
      const scrollEl = chatThreadScrollContainerElement.current;
      if (savedScrollPositionRef != null && scrollEl) {
        savedScrollPositionRef.current = scrollEl.scrollTop;
      }
    },
    [savedScrollPositionRef]
  );

  // Restore saved scroll position after mount. By the time layout effects run, the old
  // component's cleanup effect has already written scrollTop into savedScrollPositionRef.
  useLayoutEffect(() => {
    if (
      savedScrollPositionRef != null &&
      savedScrollPositionRef.current >= 0 &&
      chatThreadScrollContainerElement.current
    ) {
      chatThreadScrollContainerElement.current.scrollTop = savedScrollPositionRef.current;
      savedScrollPositionRef.current = -1;
      setHasAutoScrolled(true);
      pendingScrollRestoreRef.current = true;
    }
  }, [savedScrollPositionRef]);

  const latestUserMessageElement = latestUserMessageInfo?.element ?? null;
  const latestUserMessageIndex = latestUserMessageInfo?.index ?? null;

  // At any point in time (after the initial scroll), we want it to be possible to scroll the latest
  // user message to the top of the viewable area, so we calculate that space.
  const recalculateMinHeight = useCallback(() => {
    // For the very initial scroll-to-bottom, we want to scroll-to-bottom *without* this extra space
    // below. Use the hasAutoScrolled state to coordinate: we hold off on min-height calculations
    // until it's marked true below, indicating that we've successfully done our initial scroll.
    // It's fine to immediately add the space right after that.
    if (!hasAutoScrolled) {
      return;
    }
    if (
      latestUserMessageElement &&
      // On re-render with a different element, we may get a disconnected bounding ref with top=0.
      // In that case, just wait for the next ref and consider this one invalid.
      latestUserMessageElement.isConnected &&
      chatThreadScrollContainerElement.current &&
      chatThreadContentsElement.current
    ) {
      const lastMessageBoundingRect = latestUserMessageElement.getBoundingClientRect();
      const chatThreadContentsBoundingRect = chatThreadContentsElement.current.getBoundingClientRect();

      const lastMessagePositionPixels =
        lastMessageBoundingRect.top - chatThreadContentsBoundingRect.top - TOP_MARGIN_PX;
      setMinHeight(lastMessagePositionPixels + chatThreadScrollContainerElement.current.clientHeight);
    }
  }, [latestUserMessageElement, hasAutoScrolled]);

  // On initial render and any resize, recalculate the bottom spacing. Use layout effect to ensure that there
  // isn't a delay in updating the min-height. Otherwise, if the user vertically shrunk the window, a
  // scrollbar would appear temporarily. An alternative is to use overflow-y: scroll instead of auto.
  useLayoutEffect(() => {
    recalculateMinHeight();
  }, [recalculateMinHeight, scrollContainerWidth, scrollContainerHeight]);

  // On initial load (after the proper elements come in) and on any new message, automatically
  // scroll to the right place.
  useEffect(() => {
    // Wait until we have an actual element before any auto-scrolling. For example, there may be
    // a loading state, so wait for that to finish.
    if (latestUserMessageIndex == null || latestUserMessageElement == null) {
      return;
    }

    if (pendingScrollRestoreRef.current) {
      lastScrolledIndex.current = latestUserMessageIndex;
      pendingScrollRestoreRef.current = false;
      return;
    }

    async function scrollToLatestUserMessage(): Promise<void> {
      // Wait for the min-height to be updated, then scroll once we've rendered with the extra space.
      recalculateMinHeight();
      await sleep(1);
      // Just scroll to the bottom of the scrollable area, which is sized to be exactly the right spot
      // so the message is on top with a little margin.
      // Guard against unmount during the sleep - the ref may be null if component unmounted,
      // or scrollTo may be unavailable in some environments (e.g., during test cleanup).
      chatThreadScrollContainerElement.current?.scrollTo?.({
        top: chatThreadScrollContainerElement.current.scrollHeight,
        behavior: 'smooth',
      });
    }

    if (!hasAutoScrolled && autoScroll) {
      // Initial scroll: before we do any artificial min-height, scroll to the bottom of the
      // scrolllable area.
      chatThreadScrollContainerElement.current!.scrollTo({
        top: chatThreadScrollContainerElement.current!.scrollHeight,
        behavior: 'instant',
      });
      lastScrolledIndex.current = latestUserMessageIndex;
      setHasAutoScrolled(true);
    } else if (latestUserMessageIndex > lastScrolledIndex.current) {
      // Scroll on new message: recalculate the min-height, render, then scroll to the bottom.
      lastScrolledIndex.current = latestUserMessageIndex;
      scrollToLatestUserMessage();
    }
  }, [latestUserMessageIndex, latestUserMessageElement, recalculateMinHeight, hasAutoScrolled, autoScroll]);

  return (
    <div
      className={css(styles.chatScrollContainer)}
      {...getTestProps({component: 'ChatScrollContainer', element: 'root'})}
    >
      <div className={css(styles.chatThreadViewport)}>
        <div
          ref={setScrollContainerRef}
          className={classNames(css(styles.chatThreadScrollContainer), 'scrollbar-override')}
          {...getTestProps({component: 'ChatScrollContainer', element: 'scrollContainer'})}
        >
          <div
            ref={chatThreadContentsElement}
            className={css(styles.chatThreadContainer)}
            style={minHeight ? {minHeight: `${minHeight}px`} : undefined}
          >
            {chatMessagesElement}
            <div
              ref={bottomSentinelRef}
              aria-hidden="true"
              className={css(styles.bottomSentinel)}
              {...getTestProps({component: 'ChatScrollContainer', element: 'bottomSentinel'})}
            />
          </div>
        </div>
        {showScrollDownButton && (
          <Tooltip label="Scroll down">
            <ActionIcon
              aria-label="Scroll down"
              className={css(styles.scrollToBottomButton)}
              onClick={scrollToBottom}
              radius="xl"
              size="lg"
              variant="default"
            >
              <BenchlingIcon icon="chevron-down" />
            </ActionIcon>
          </Tooltip>
        )}
      </div>
      {chatInputElement && (
        <div className={css(styles.chatInputContainer)}>
          <div className={css(styles.chatInputContainerFade)} />
          {chatInputElement}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  chatScrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 0,
    flex: 1,
    width: '100%',
  },
  chatThreadViewport: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    width: '100%',
  },
  // The scrolling portion of the chat thread includes the blank space to the left and right
  // of the chat messages, so it needs overflowY without the maxWidth set yet.
  chatThreadScrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    overflowY: 'auto',
  },
  chatThreadContainer: {
    width: '100%',
    maxWidth: '800px',
  },
  bottomSentinel: {
    height: 1,
    width: '100%',
  },
  chatInputContainer: {
    width: '100%',
    maxWidth: '800px',
    padding: 16,
    paddingTop: 0,
    paddingBottom: 0,
    position: 'relative',
  },
  chatInputContainerFade: {
    position: 'absolute',
    top: -16,
    height: 16,
    left: 0,
    right: SCROLLBAR_WIDTH,
    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
  },
  scrollToBottomButton: {
    bottom: 16,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    left: '50%',
    position: 'absolute',
    transform: 'translateX(-50%)',
    zIndex: 1,
  },
});
